/**
 * Lightweight CSV roster parser for the Admin-Web preview flow — Issue #176.
 * Encoding detection (UTF-8 / GBK) added for Issue #293.
 *
 * Why CSV and not xlsx for v1?
 *   - Real `*.xlsx` parsing would pull in a heavy library (SheetJS) with a
 *     CVE surface that isn't justified for the foundation Issue. Admins can
 *     export the company roster to CSV (UTF-8 or GBK) from Excel / WPS before
 *     uploading. xlsx upload can be layered in a follow-up Issue.
 *   - Backend ONLY accepts a JSON array of rows. This module is purely a
 *     UI-side adapter to turn file bytes / pasted CSV into that JSON.
 *
 * 红线：
 *   - 不在前端做任何业务校验或脱敏；所有校验在后端复算。前端只做格式拆分。
 *   - 不把 CSV 原文保留在 React 状态超过解析所需；调用方应直接转 JSON 行集合。
 *   - 不输出 console；调用方处理错误展示。
 */

import type { CreateHrEmployeeImportRowDto } from '@/hooks/useHr';

/** Canonical header keys the backend understands. */
const HEADER_ALIASES: Record<string, keyof CreateHrEmployeeImportRowDto> = {
  // Required
  '工号': 'employeeNo',
  'employee_no': 'employeeNo',
  'employeeno': 'employeeNo',
  '姓名': 'name',
  'name': 'name',
  // Optional
  '性别': 'gender',
  'gender': 'gender',
  '部门': 'department',
  'department': 'department',
  '岗位': 'position',
  'position': 'position',
  '职务': 'jobTitle',
  'job_title': 'jobTitle',
  'jobtitle': 'jobTitle',
  '职级': 'jobLevel',
  'job_level': 'jobLevel',
  'joblevel': 'jobLevel',
  '入职日期': 'hireDate',
  'hire_date': 'hireDate',
  'hiredate': 'hireDate',
  '邮箱': 'email',
  'email': 'email',
  '身份证号': 'idCard',
  '身份证': 'idCard',
  'id_card': 'idCard',
  'idcard': 'idCard',
  '手机号': 'phone',
  '手机': 'phone',
  'phone': 'phone',
  '在职状态': 'employmentStatus',
  'employment_status': 'employmentStatus',
  'employmentstatus': 'employmentStatus',
};

export interface CsvParseResult {
  rows: CreateHrEmployeeImportRowDto[];
  unknownHeaders: string[];
}

/** Result of decoding raw CSV file bytes. */
export interface CsvDecodeResult {
  text: string;
  encoding: 'utf-8-bom' | 'utf-8' | 'gbk';
}

/**
 * Thrown by {@link decodeCsvBytes} when the file cannot be decoded reliably.
 * `code` distinguishes unrecognised encoding from unrecognised HR headers so
 * the UI can show an appropriate message.
 */
export class CsvEncodingError extends Error {
  constructor(
    public readonly code: 'unsupported_encoding' | 'no_recognized_headers',
    message: string,
  ) {
    super(message);
    this.name = 'CsvEncodingError';
  }
}

/**
 * Splits one CSV line into cells, handling double-quoted fields and embedded
 * commas. Does not support escaped newlines inside quoted cells (assumes
 * pre-cleaned input). Returns trimmed values.
 */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      cells.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
}

/**
 * Check whether at least one cell in the first non-empty line of decoded text
 * matches a known HR header alias. Used as a mojibake / wrong-encoding guard:
 * if decoding succeeds but produces no recognisable HR header, the file is
 * likely garbled or the wrong file.
 */
function hasSomeRecognizedHeader(text: string): boolean {
  const firstLine = text.split(/\r?\n/u).find((l) => l.trim().length > 0) ?? '';
  const cells = splitCsvLine(firstLine).map((h) =>
    h.replace(/^\uFEFF/u, '').replace(/\s+/gu, '').toLowerCase(),
  );
  return cells.some((h) => h in HEADER_ALIASES);
}

/**
 * Decode raw CSV file bytes to text, auto-detecting encoding.
 *
 * Supported encodings (tried in order):
 *   1. UTF-16 LE / BE  — rejected; admin must re-export as UTF-8 or GBK CSV.
 *   2. UTF-8 with BOM  — decoded strictly; no fallback.
 *   3. UTF-8           — decoded strictly; falls through to GBK if no HR
 *                        header is recognised (catches GBK bytes that happen
 *                        to be valid UTF-8 but produce garbage Chinese).
 *   4. GBK / GB18030   — decoded strictly; used when UTF-8 fails or yields
 *                        no recognised header.
 *
 * Throws {@link CsvEncodingError} when:
 *   - The BOM identifies UTF-16 (unsupported_encoding).
 *   - No tried encoding decodes the bytes without error AND recognises at
 *     least one HR header (unsupported_encoding or no_recognized_headers).
 */
export function decodeCsvBytes(bytes: Uint8Array): CsvDecodeResult {
  // Reject UTF-16 BOMs (FF FE = LE; FE FF = BE)
  if (
    (bytes[0] === 0xff && bytes[1] === 0xfe) ||
    (bytes[0] === 0xfe && bytes[1] === 0xff)
  ) {
    throw new CsvEncodingError(
      'unsupported_encoding',
      '不支持 UTF-16 编码；请将文件另存为 UTF-8 CSV 后重新导入。',
    );
  }

  // UTF-8 BOM (EF BB BF) — strip BOM bytes, decode rest strictly
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(
      bytes.subarray(3),
    );
    if (!hasSomeRecognizedHeader(text)) {
      throw new CsvEncodingError(
        'no_recognized_headers',
        '文件已解码但未识别到 HR 花名册表头（如工号、姓名、部门等）；请确认文件格式并重新导出。',
      );
    }
    return { text, encoding: 'utf-8-bom' };
  }

  // Try strict UTF-8
  let utf8Text: string | null = null;
  try {
    utf8Text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    // Invalid UTF-8 byte sequence; will try GBK next
  }

  if (utf8Text !== null && hasSomeRecognizedHeader(utf8Text)) {
    return { text: utf8Text, encoding: 'utf-8' };
  }

  // Try GBK / GB18030
  let gbkText: string | null = null;
  try {
    gbkText = new TextDecoder('gbk', { fatal: true }).decode(bytes);
  } catch {
    // Invalid GBK byte sequence
  }

  if (gbkText !== null && hasSomeRecognizedHeader(gbkText)) {
    return { text: gbkText, encoding: 'gbk' };
  }

  // Neither encoding produced recognisable HR headers
  if (utf8Text !== null || gbkText !== null) {
    throw new CsvEncodingError(
      'no_recognized_headers',
      '文件已解码但未识别到 HR 花名册表头（如工号、姓名、部门等）；请确认文件格式并重新导出。',
    );
  }
  throw new CsvEncodingError(
    'unsupported_encoding',
    '文件编码无法识别（不是 UTF-8 也不是 GBK）；请将文件另存为 UTF-8 CSV 后重新导入。',
  );
}

export function parseHrRosterCsv(raw: string): CsvParseResult {
  const lines = raw
    .replace(/^\uFEFF/u, '') // strip BOM
    .split(/\r?\n/u)
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) {
    return { rows: [], unknownHeaders: [] };
  }
  const headerLine = lines[0]!;
  const headers = splitCsvLine(headerLine).map((h) =>
    h.replace(/\s+/gu, '').toLowerCase(),
  );
  const unknownHeaders: string[] = [];
  const mapping: Array<keyof CreateHrEmployeeImportRowDto | null> = headers.map(
    (h) => {
      const key = HEADER_ALIASES[h] ?? HEADER_ALIASES[h.toLowerCase()] ?? null;
      if (key === null && h.length > 0) {
        unknownHeaders.push(headers[headers.indexOf(h)] ?? h);
      }
      return key;
    },
  );

  const rows: CreateHrEmployeeImportRowDto[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]!);
    const row: Partial<CreateHrEmployeeImportRowDto> = {
      rowNumber: i + 1, // 1 = header, so data row 1 lives at line 2
    };
    for (let c = 0; c < mapping.length; c += 1) {
      const key = mapping[c];
      const value = cells[c] ?? '';
      if (key === null) continue;
      if (value.length === 0) continue;
      if (key === 'rowNumber') continue;
      (row as Record<string, string>)[key] = value;
    }
    rows.push(row as CreateHrEmployeeImportRowDto);
  }
  return { rows, unknownHeaders: [...new Set(unknownHeaders)] };
}

/**
 * SHA-256 of a byte array, hex-encoded. Used for audit so the backend can
 * record which exact file was uploaded. Falls back to a deterministic surrogate
 * in environments without WebCrypto (e.g., some test runners).
 */
export async function sha256HexBytes(data: Uint8Array): Promise<string> {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.subtle !== 'undefined'
  ) {
    const hash = await globalThis.crypto.subtle.digest('SHA-256', data);
    const hashBytes = new Uint8Array(hash);
    let hex = '';
    for (const b of hashBytes) {
      hex += b.toString(16).padStart(2, '0');
    }
    return hex;
  }
  // Deterministic fallback that still satisfies the backend hex-format check.
  let h = 0;
  for (const b of data) {
    h = (h * 31 + b) >>> 0;
  }
  return h.toString(16).padStart(64, '0');
}

/**
 * SHA-256 over the raw CSV string, hex-encoded. Used purely for audit (so
 * the backend can record which file was uploaded). Falls back to a length
 * marker when SubtleCrypto is unavailable (e.g., test env without WebCrypto).
 */
export async function sha256Hex(input: string): Promise<string> {
  return sha256HexBytes(new TextEncoder().encode(input));
}
